import React from 'react';
import { View, StyleSheet, useWindowDimensions, Platform } from 'react-native';
import { useResponsive } from '@/hooks/common/useResponsive';
import { GradientBackground } from '@/components/shared/redesign';

interface DesktopAdminLayoutProps {
  children: React.ReactNode;
  /**
   * Web only: make the layout exactly one window tall so the screen's panes
   * scroll internally instead of the page growing with their content. Opt-in,
   * because screens with tall static content rely on page scrolling today.
   */
  fillViewport?: boolean;
}

export const DesktopAdminLayout: React.FC<DesktopAdminLayoutProps> = React.memo(({
  children,
  fillViewport = false,
}) => {
  const { layoutMode } = useResponsive();
  const { height: windowHeight } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';
  // The default container keeps `flex: 1`; on web react-native-web turns that
  // into `flex-basis: 0%`, which inside the stack navigator's indefinite-height
  // card resolves to the content size and overrides the window height below —
  // the page grows with its content. `fillViewport` drops the flex box for a
  // real viewport-height one.
  const containerStyle = isWeb && fillViewport
    ? [styles.viewportContainer, { height: windowHeight }]
    : [styles.contentContainer, isWeb ? { height: windowHeight } : undefined];

  if (layoutMode !== 'desktop') {
    return <>{children}</>;
  }

  return (
    <View style={containerStyle}>
      <GradientBackground>
        <View style={styles.contentArea}>
          {children}
        </View>
      </GradientBackground>
    </View>
  );
});
DesktopAdminLayout.displayName = 'DesktopAdminLayout';

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  viewportContainer: {
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  contentArea: {
    flex: 1,
    padding: 24,
    maxWidth: '100%',
    overflow: 'visible',
    minHeight: 0,
  },
});
